
import { Link, useLocation } from "react-router-dom";
import { BookOpen, ChevronLeft, ChevronRight, Check, Copy, ExternalLink } from "lucide-react";
import type { KnowledgeChunkDetailResponse, KnowledgeChunkResponse } from "../../gen/models";

interface ChunkHeaderProps {
  chunk: KnowledgeChunkDetailResponse;
  prevChunk: KnowledgeChunkResponse | null;
  nextChunk: KnowledgeChunkResponse | null;
  copiedId: boolean;
  copiedText: boolean;
  onNavigateChunk: (id: string) => void;
  onCopyId: () => void;
  onCopyText: () => void;
}

export function ChunkHeader({
  chunk,
  prevChunk,
  nextChunk,
  copiedId,
  copiedText,
  onNavigateChunk,
  onCopyId,
  onCopyText,
}: ChunkHeaderProps) {
  const location = useLocation();
  return (
    <>
      {/* Top Breadcrumb Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
          <Link
            to={{ pathname: "/knowledge", search: location.search }}
            className="hover:text-stone-900 dark:hover:text-stone-100 transition inline-flex items-center gap-1"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Knowledge Base</span>
          </Link>
          <span>/</span>
          {chunk.item_id ? (
            <Link
              to={{ pathname: `/knowledge/${chunk.item_id}`, search: location.search }}
              className="hover:text-stone-900 dark:hover:text-stone-100 transition font-medium text-stone-700 dark:text-stone-300 truncate max-w-[240px]"
              title={chunk.item_title || "Knowledge Document"}
            >
              {chunk.item_title || "Document"}
            </Link>
          ) : (
            <span>Document</span>
          )}
          <span>/</span>
          <span className="font-mono text-cyan-600 dark:text-cyan-400 font-semibold">
            Chunk #{chunk.chunk_index}
          </span>
        </div>

        {/* Prev / Next Quick Controls */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!prevChunk}
            onClick={() => prevChunk && onNavigateChunk(prevChunk.id)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 disabled:opacity-40 disabled:pointer-events-none transition text-xs shadow-2xs"
            title={prevChunk ? `Previous Chunk #${prevChunk.chunk_index}` : "No previous chunk"}
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            <span>Prev Chunk</span>
          </button>
          <button
            type="button"
            disabled={!nextChunk}
            onClick={() => nextChunk && onNavigateChunk(nextChunk.id)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 disabled:opacity-40 disabled:pointer-events-none transition text-xs shadow-2xs"
            title={nextChunk ? `Next Chunk #${nextChunk.chunk_index}` : "No next chunk"}
          >
            <span>Next Chunk</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Main Header Card */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-6 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="space-y-1.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-cyan-950 text-cyan-300 border border-cyan-800/60 font-mono">
              Chunk #{chunk.chunk_index}
            </span>
            {chunk.item_category && (
              <span className="px-2.5 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider bg-stone-900 text-white dark:bg-stone-800 dark:text-stone-200">
                {chunk.item_category}
              </span>
            )}
            <h1 className="text-lg font-bold tracking-tight text-stone-900 dark:text-stone-100 flex items-center gap-2">
              <span>{chunk.item_title || "Knowledge Document Chunk"}</span>
            </h1>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize border ${
                chunk.has_embedding
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-400 dark:border-emerald-800/80"
                  : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/60 dark:text-amber-400 dark:border-amber-800/80"
              }`}
            >
              {chunk.has_embedding ? "Indexed & Embedded" : "No Embedding"}
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400 dark:text-stone-500 pt-1">
            <div className="flex items-center gap-1.5">
              <span>Chunk ID:</span>
              <code className="font-mono text-stone-700 dark:text-stone-300 font-semibold">
                {chunk.id}
              </code>
              <button
                type="button"
                onClick={onCopyId}
                className="p-1 rounded hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 transition"
                title="Copy Chunk UUID"
              >
                {copiedId ? (
                  <Check className="w-3 h-3 text-emerald-500" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </button>
            </div>
            <span>•</span>
            <span>Created: {new Date(chunk.created_at).toLocaleString()}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onCopyText}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 text-xs font-semibold text-stone-700 dark:text-stone-200 hover:bg-stone-50 dark:hover:bg-stone-800 transition shadow-2xs"
          >
            {copiedText ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-500" />
                <span className="text-emerald-600 dark:text-emerald-400">Copied Text</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Copy Chunk Text</span>
              </>
            )}
          </button>

          {chunk.item_id && (
            <Link
              to={{ pathname: `/knowledge/${chunk.item_id}`, search: location.search }}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-stone-900 text-white dark:bg-stone-100 dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <span>View Full Document</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    </>
  );
}
